import { useCallback, useState } from 'react';

import { useGetDeliveryRestaurant } from 'features/store/hooks/use-select-delivery-store';

import { IRestaurant } from '@rbi-ctg/store';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';
import { convertMilesToMeters } from 'utils/distance';
import { IPlaceAddress } from 'utils/geolocation';
import logger from 'utils/logger';

export const useFindAvailableDeliveryRestaurant = ({
  setDeliverySearchCompleted,
}: {
  setDeliverySearchCompleted: (isDeliverySearchComplete: boolean) => void;
}) => {
  const [availableRestaurant, setAvailableRestaurant] = useState<IRestaurant | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { deliveryAddress } = useOrderContext();
  const deliveryRadius = useFlag(LaunchDarklyFlag.DELIVERY_RADIUS_IN_MILES);
  const getDeliveryRestaurant = useGetDeliveryRestaurant();

  const clearSearchState = useCallback(() => {
    setAvailableRestaurant(null);
    setDeliverySearchCompleted(false);
  }, [setDeliverySearchCompleted]);

  const findFirstOrderableRestaurantForDelivery = useCallback(async () => {
    setIsLoading(true);

    try {
      if (!deliveryAddress?.latitude || !deliveryAddress?.longitude) {
        return;
      }

      const requestParams = {
        coordinates: {
          lat: deliveryAddress.latitude,
          lng: deliveryAddress.longitude,
        },
        deliveryRadius: convertMilesToMeters(deliveryRadius),
        address: deliveryAddress as IPlaceAddress,
        phoneNumber: deliveryAddress.phoneNumber ?? '',
      };

      const { data, error } = await getDeliveryRestaurant(requestParams);

      if (error) {
        logger.error({
          message: 'Failed to get delivery restaurant as replacement for unavailable store',
          error,
        });
        return;
      }

      if (data?.quote && data?.restaurant) {
        setAvailableRestaurant(data.restaurant as IRestaurant);
      } else {
        logger.warn({
          message: 'No quote or restaurant available for delivery',
          quote: data?.quote,
          restaurant: data?.restaurant?._id,
        });
      }
    } finally {
      setIsLoading(false);
      setDeliverySearchCompleted(true);
    }
  }, [deliveryAddress, deliveryRadius, getDeliveryRestaurant, setDeliverySearchCompleted]);

  return {
    findFirstOrderableRestaurantForDelivery,
    availableRestaurant,
    clearSearchState,
    isLoading,
  };
};
